import React, { useState, useEffect } from 'react';
import { Download, X, MapPin } from 'lucide-react';
import { Coordinates } from '../types';
import { COUNTY_DOWNLOAD_BOUNDS } from '../constants';
import { useTranslation } from 'react-i18next';

interface CountyDownloadModalProps {
  userPosition: Coordinates;
  onClose: () => void;
  onDownload: (countyName: string, bounds: { latMin: number; latMax: number; lonMin: number; lonMax: number }) => void;
}

function detectCounty(lat: number, lon: number): { name: string; bounds: { latMin: number; latMax: number; lonMin: number; lonMax: number } } | null {
  for (const [name, bounds] of Object.entries(COUNTY_DOWNLOAD_BOUNDS)) {
    if (lat >= bounds.latMin && lat <= bounds.latMax && lon >= bounds.lonMin && lon <= bounds.lonMax) {
      return { name, bounds };
    }
  }
  return null;
}

const CountyDownloadModal: React.FC<CountyDownloadModalProps> = ({ userPosition, onClose, onDownload }) => {
  const { t } = useTranslation();
  const [county, setCounty] = useState<{ name: string; bounds: { latMin: number; latMax: number; lonMin: number; lonMax: number } } | null>(null);

  useEffect(() => {
    const detected = detectCounty(userPosition.latitude, userPosition.longitude);
    setCounty(detected);
    if (!detected) onClose();
  }, [userPosition, onClose]);

  if (!county) return null;

  return (
    <div className="fixed inset-0 z-[10005] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="p-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center">
            <Download size={24} className="text-emerald-600" />
          </div>
          <h3 className="text-lg font-black text-stone-800 mb-1">
            Last ned {county.name}?
          </h3>
          <p className="text-sm text-stone-500 leading-relaxed mb-6">
            Du har Premium! Last ned kart, punkter og ruter for {county.name} slik at appen fungerer uten nett.
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-stone-400 mb-6">
            <MapPin size={12} />
            <span>{t("auto.countydownloadmodal.basert_pa_din_posisjon", "Basert på din posisjon")}</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-stone-200 text-stone-600 text-sm font-bold hover:bg-stone-50 transition-colors"
            >
              {t("auto.countydownloadmodal.ikke_na", "Ikke nå")}
            </button>
            <button
              onClick={() => {
                onDownload(county.name, county.bounds);
                onClose();
              }}
              className="flex-1 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-500 transition-colors active:scale-95 flex items-center justify-center gap-2"
            >
              <Download size={14} /> {t("auto.countydownloadmodal.last_ned", "Last ned")}
            </button>
          </div>
        </div>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default CountyDownloadModal;
